import { useEffect } from 'react';

import { hasOnScreenKeyboard } from './useVisualViewport';

/** With no visualViewport resize (the keyboard was already open), check after this long. */
export const KEYBOARD_SETTLE_FALLBACK_MS = 300;
/** After the last visualViewport resize, the keyboard has finished moving once this long passes. */
export const KEYBOARD_RESIZE_QUIET_MS = 100;
/** A field closer than this to the top of the visible area or the keyboard counts as hidden. */
const EDGE_GAP_PX = 16;

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** A field that opens the on-screen keyboard (or, for a select, the picker in its place). */
export const isTextEntryField = (element: EventTarget | null): element is HTMLElement => {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) return true;
  if (element instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(element.type);
  if (!(element instanceof HTMLElement)) return false;
  const editable = element.getAttribute('contenteditable');
  return element.isContentEditable || (editable !== null && editable !== 'false');
};

/** The visible part of the layout viewport, in the same coordinates as getBoundingClientRect. */
const visibleArea = () => {
  const viewport = window.visualViewport;
  const top = viewport ? viewport.offsetTop : 0;
  const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
  return { top: top + EDGE_GAP_PX, bottom: bottom - EDGE_GAP_PX };
};

/**
 * True when the field is on screen above the keyboard and nothing is drawn over it (a bar held
 * above the keyboard, a sticky footer, the edge of its scrolling container).
 */
export const isFieldVisibleAboveKeyboard = (field: HTMLElement): boolean => {
  const rect = field.getBoundingClientRect();
  const area = visibleArea();
  // A field taller than the visible area (a long textarea) only needs its top showing.
  const bottom = Math.min(rect.bottom, rect.top + (area.bottom - area.top));
  if (rect.top < area.top || bottom > area.bottom) return false;
  if (typeof document.elementFromPoint !== 'function') return true;
  const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  return !hit || hit === field || field.contains(hit) || hit.contains(field);
};

const scrollFieldIntoView = (field: HTMLElement) => {
  const area = visibleArea();
  const isTallerThanArea = field.getBoundingClientRect().height > area.bottom - area.top;
  // Scrolls every scrolling ancestor: the sheet's body or full-screen page, then the window.
  field.scrollIntoView?.({ block: isTallerThanArea ? 'start' : 'center', behavior: 'smooth' });
};

/**
 * On a touch screen, when a field takes focus, waits for the on-screen keyboard to finish opening
 * and then scrolls the field into the middle of what's left of the screen, unless it's already in
 * view. Listens on the whole document, so every screen, sheet and dialog gets it. Returns a
 * function that stops listening.
 */
export const watchFocusedFields = (target: Document = document): (() => void) => {
  let cancelPending: (() => void) | null = null;

  const onFocusIn = (event: FocusEvent) => {
    cancelPending?.();
    const field = event.target;
    if (!isTextEntryField(field) || !hasOnScreenKeyboard()) return;

    const viewport = window.visualViewport;
    let timer = 0;
    const stop = () => {
      window.clearTimeout(timer);
      viewport?.removeEventListener('resize', onResize);
      field.removeEventListener('focusout', stop);
      if (cancelPending === stop) cancelPending = null;
    };
    const settle = () => {
      stop();
      if (target.activeElement !== field || isFieldVisibleAboveKeyboard(field)) return;
      scrollFieldIntoView(field);
    };
    // The keyboard resizes the visual viewport as it opens; wait until it stops.
    function onResize() {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, KEYBOARD_RESIZE_QUIET_MS);
    }

    timer = window.setTimeout(settle, KEYBOARD_SETTLE_FALLBACK_MS);
    viewport?.addEventListener('resize', onResize);
    field.addEventListener('focusout', stop);
    cancelPending = stop;
  };

  target.addEventListener('focusin', onFocusIn);
  return () => {
    cancelPending?.();
    target.removeEventListener('focusin', onFocusIn);
  };
};

/** Keeps a focused field above the on-screen keyboard. Mount it once, at the app root (`KeyboardAwareLayout`). */
export const useKeepFocusedFieldVisible = () => {
  useEffect(() => watchFocusedFields(document), []);
};
